// ShopCheckout.jsx — /shop/checkout
// Custom checkout on our domain. Collects shipping address, calls Shippo for
// live rates, shows itemized review, then creates a Square payment link with
// the chosen Shippo rate injected as a shipping service charge and redirects
// to Square Hosted Checkout for the card entry.
//
// Next slice replaces the Square redirect with an inline Web Payments SDK
// card form — at that point the entire flow lives on our domain.

import { useEffect, useMemo, useRef, useState } from "react";
import { doc as fsDoc, getDoc } from "firebase/firestore";
import { getFunctions, httpsCallable } from "firebase/functions";
import { getApp } from "firebase/app";
import { db } from "../../firebase";
import { useCart, cartSubtotalCents, formatPrice, readCart, clearCart } from "../../utils/cart";
import PanBar from "./components/PanBar";

// Square Web Payments SDK script URL. Application ID + Location ID come from
// getShopCheckoutConfig (so no hardcoded drift if Dan's Square setup changes).
const SQUARE_SDK_URL = "https://web.squarecdn.com/v1/square.js";

// Load the Square Web Payments SDK exactly once. Returns the Square global.
let sdkPromise = null;
function loadSquareSDK() {
  if (typeof window !== "undefined" && window.Square) return Promise.resolve(window.Square);
  if (sdkPromise) return sdkPromise;
  sdkPromise = new Promise((resolve, reject) => {
    const s = document.createElement("script");
    s.src = SQUARE_SDK_URL;
    s.async = true;
    s.onload  = () => resolve(window.Square);
    s.onerror = () => reject(new Error("Couldn't load Square's payment form. Check your connection and try again."));
    document.head.appendChild(s);
  });
  return sdkPromise;
}

const BG = `radial-gradient(ellipse 140% 95% at 50% 0%,
  #1f59db 0%,
  #1a4b9e 30%,
  #0e2e46 70%,
  #081a2a 100%)`;

const COUNTRIES = [
  { code: "US", name: "United States" },
  { code: "CA", name: "Canada" },
  { code: "GB", name: "United Kingdom" },
  { code: "AU", name: "Australia" },
  { code: "DE", name: "Germany" },
  { code: "FR", name: "France" },
  { code: "NL", name: "Netherlands" },
  { code: "IE", name: "Ireland" },
  { code: "NZ", name: "New Zealand" },
  { code: "JP", name: "Japan" },
];
const US_STATES = ["AL","AK","AZ","AR","CA","CO","CT","DE","FL","GA","HI","ID","IL","IN","IA","KS","KY","LA","ME","MD","MA","MI","MN","MS","MO","MT","NE","NV","NH","NJ","NM","NY","NC","ND","OH","OK","OR","PA","RI","SC","SD","TN","TX","UT","VT","VA","WA","WV","WI","WY","DC"];

export default function ShopCheckout({ navigate }) {
  const items = useCart();
  const subtotalCents = cartSubtotalCents(items);

  const [step, setStep] = useState("shipping"); // "shipping" | "rates" | "review" | "payment"
  const [address, setAddress] = useState({
    name: "", email: "", phone: "",
    line1: "", line2: "", city: "", state: "", postalCode: "", country: "US",
  });
  const [rates, setRates] = useState(null);
  const [selectedRateId, setSelectedRateId] = useState(null);
  const [rateError, setRateError] = useState(null);
  const [rateLoading, setRateLoading] = useState(false);
  const [payBusy, setPayBusy] = useState(false);
  const [payError, setPayError] = useState(null);
  const [couponCode, setCouponCode] = useState("");
  const [couponDiscount, setCouponDiscount] = useState(0); // cents off for display
  const [couponFreeShipping, setCouponFreeShipping] = useState(false);

  // Bounce empty carts home
  useEffect(() => {
    if (readCart().length === 0) {
      const t = setTimeout(() => navigate ? navigate("/shop") : (window.location.href = "/shop"), 600);
      return () => clearTimeout(t);
    }
  }, [navigate]);

  function update(field, value) {
    setAddress((a) => ({ ...a, [field]: value }));
  }

  function shippingFormValid() {
    const required = ["name", "email", "line1", "city", "postalCode", "country"];
    if (address.country === "US" && !address.state) return false;
    for (const k of required) if (!address[k]?.trim()) return false;
    if (!/^\S+@\S+\.\S+$/.test(address.email.trim())) return false;
    return true;
  }

  async function fetchRates() {
    if (!shippingFormValid()) return;
    setRateLoading(true);
    setRateError(null);
    setRates(null);
    setSelectedRateId(null);
    try {
      const fn = httpsCallable(getFunctions(getApp()), "calculateShopShipping");
      const res = await fn({
        items: items.map((l) => ({ variationId: l.variationId, quantity: l.quantity, isPOD: l.isPOD })),
        address: cleanAddress(address),
      });
      if (!res.data?.ok) {
        setRateError(res.data?.reason || "Couldn't get shipping rates for this address.");
        return;
      }
      setRates(res.data.rates);
      setSelectedRateId(res.data.rates[0]?.rateId || null); // default to cheapest (already sorted)
      setStep("rates");
    } catch (err) {
      setRateError(err?.message || "Shipping lookup failed. Try again in a moment.");
    } finally {
      setRateLoading(false);
    }
  }

  const selectedRate = rates?.find((r) => r.rateId === selectedRateId) || null;
  const totalCents = Math.max(0, subtotalCents - couponDiscount) + (couponFreeShipping ? 0 : (selectedRate?.amountCents || 0));

  function goToPayment() {
    setPayError(null);
    // $0 orders skip the card form entirely
    if (totalCents <= 0) {
      submitOrder({ paymentToken: null, verificationToken: null });
    } else {
      setStep("payment");
    }
  }

  async function submitPayment({ paymentToken, verificationToken }) {
    return submitOrder({ paymentToken, verificationToken });
  }

  async function submitOrder({ paymentToken, verificationToken }) {
    if (!selectedRate || payBusy) return;
    setPayBusy(true);
    setPayError(null);
    try {
      const fn = httpsCallable(getFunctions(getApp()), "processShopPayment");
      const res = await fn({
        items: items.map((l) => ({
          variationId: l.variationId,
          quantity:    l.quantity,
          name:        l.name,
          isPOD:       l.isPOD,
        })),
        address: cleanAddress(address),
        shippingCents: couponFreeShipping ? 0 : selectedRate.amountCents,
        rateId: couponFreeShipping ? null : selectedRate.rateId, // server re-verifies the amount against Shippo
        shippingLabel: `Shipping (${selectedRate.provider} ${selectedRate.servicelevel})`,
        paymentToken: paymentToken || null,
        verificationToken: verificationToken || null,
        ...(couponCode.trim() ? { couponCode: couponCode.trim() } : {}),
      });
      if (!res.data?.ok) throw new Error("Order didn't complete. Try again.");
      clearCart();
      const { squareOrderId, paymentId } = res.data;
      window.location.href = `/shop/thanks?orderId=${encodeURIComponent(squareOrderId)}&paymentId=${encodeURIComponent(paymentId || "")}`;
    } catch (err) {
      setPayError(err?.message || "Order didn't go through. Try again or email hello@thejoyofpancakes.com.");
      setPayBusy(false);
    }
  }

  const isCart = items.length > 0;

  return (
    <div style={{
      minHeight: "100vh",
      background: BG,
      fontFamily: "var(--jop-display)",
      color: "var(--jop-cream)",
      padding: "3rem 1rem 5rem",
      boxSizing: "border-box",
    }}>
      <div style={{ maxWidth: "620px", margin: "0 auto" }}>

        {/* Back to shop */}
        <div style={{ marginBottom: "1.25rem", textAlign: "center" }}>
          <button
            onClick={() => navigate ? navigate("/shop") : (window.location.href = "/shop")}
            style={{
              background: "transparent",
              border: "1px solid rgba(255,218,173,0.25)",
              color: "rgba(255,218,173,0.75)",
              borderRadius: "999px",
              padding: "0.4rem 0.9rem",
              fontFamily: "var(--jop-display)",
              fontSize: "0.82rem",
              fontWeight: 600,
              cursor: "pointer",
            }}
          >
            ← Back to shop
          </button>
        </div>

        <div style={{
          background: "#ffffff",
          color: "#1a1a1a",
          borderRadius: "18px",
          padding: "2.5rem 2rem 2.75rem",
          boxShadow: "0 8px 48px rgba(0,0,0,0.3)",
        }}>
          <div style={{ textAlign: "center", marginBottom: "1.5rem" }}>
            <PanBar width={60} height={3} margin="0 auto 0.85rem" />
            <h1 style={{
              fontFamily: "var(--jop-script)",
              fontSize: "clamp(2rem, 5.5vw, 2.5rem)",
              color: "#1d52a0",
              fontWeight: 700,
              margin: "0 0 0.4rem",
              lineHeight: 1.1,
            }}>
              Checkout
            </h1>
            <StepDots current={step} />
          </div>

          {!isCart && (
            <div style={{ textAlign: "center", padding: "2rem 0", color: "#666" }}>
              Your cart is empty — redirecting…
            </div>
          )}

          {isCart && step === "shipping" && (
            <ShippingForm
              address={address}
              update={update}
              onNext={fetchRates}
              loading={rateLoading}
              error={rateError}
              canSubmit={shippingFormValid()}
            />
          )}

          {isCart && step === "rates" && rates && (
            <RatesStep
              rates={rates}
              selectedRateId={selectedRateId}
              onSelect={setSelectedRateId}
              onBack={() => { setStep("shipping"); setRates(null); }}
              onNext={() => setStep("review")}
            />
          )}

          {isCart && step === "review" && selectedRate && (
            <ReviewStep
              items={items}
              address={address}
              selectedRate={selectedRate}
              subtotalCents={subtotalCents}
              couponCode={couponCode}
              couponDiscount={couponDiscount}
              onCouponChange={setCouponCode}
              onCouponDiscountChange={setCouponDiscount}
              onCouponFreeShippingChange={setCouponFreeShipping}
              couponFreeShipping={couponFreeShipping}
              totalCents={totalCents}
              onBack={() => setStep("rates")}
              onContinue={goToPayment}
              busy={payBusy}
              error={payError}
            />
          )}

          {isCart && step === "payment" && selectedRate && (
            <PaymentStep
              totalCents={totalCents}
              onBack={() => setStep("review")}
              onSubmit={submitPayment}
              busy={payBusy}
              error={payError}
            />
          )}
        </div>

        {isCart && step === "shipping" && (
          <div style={{ marginTop: "1.5rem", background: "rgba(0,0,0,0.35)", border: "1px solid rgba(255,218,173,0.15)", borderRadius: "12px", padding: "0.95rem 1.2rem", color: "rgba(255,218,173,0.75)", fontSize: "0.85rem", textAlign: "center" }}>
            {items.length} item{items.length === 1 ? "" : "s"} · Subtotal {formatPrice(subtotalCents)} · shipping calculated on the next step
          </div>
        )}
      </div>
    </div>
  );
}

// ── Steps ──────────────────────────────────────────────────────────────

function StepDots({ current }) {
  const steps = [
    { key: "shipping", label: "Address" },
    { key: "rates",    label: "Shipping" },
    { key: "review",   label: "Review" },
    { key: "payment",  label: "Pay" },
  ];
  return (
    <div style={{ display: "flex", justifyContent: "center", gap: "0.45rem", marginTop: "0.35rem" }}>
      {steps.map((s, i) => {
        const active = s.key === current;
        const done = steps.findIndex((x) => x.key === current) > i;
        return (
          <div key={s.key} style={{
            display: "flex", alignItems: "center", gap: "0.35rem",
            color: active ? "#1d52a0" : done ? "#70be44" : "#999",
            fontSize: "0.76rem",
            fontWeight: active ? 700 : 600,
          }}>
            <span style={{
              width: "7px", height: "7px", borderRadius: "50%",
              background: active ? "#1d52a0" : done ? "#70be44" : "#ccc",
            }} />
            {s.label}
          </div>
        );
      })}
    </div>
  );
}

// Rotating status messages while Shippo thinks. Shippo typically responds
// in 2-4s; these keep the loading state alive without lying about progress.
const LOADING_MESSAGES = [
  "Checking USPS rates…",
  "Comparing service tiers…",
  "Picking the cheapest option…",
  "Almost there…",
];
function useRotatingLoadingMessage(active) {
  const [idx, setIdx] = useState(0);
  useEffect(() => {
    if (!active) { setIdx(0); return; }
    const t = setInterval(() => {
      setIdx((i) => Math.min(i + 1, LOADING_MESSAGES.length - 1));
    }, 1200);
    return () => clearInterval(t);
  }, [active]);
  return LOADING_MESSAGES[idx];
}

function ShippingForm({ address, update, onNext, loading, error, canSubmit }) {
  const isUS = address.country === "US";
  const loadingMsg = useRotatingLoadingMessage(loading);
  return (
    <form onSubmit={(e) => { e.preventDefault(); onNext(); }}>
      <SectionLabel>Contact</SectionLabel>
      <Row single>
        <Field label="Your name" required>
          <Input value={address.name} onChange={(v) => update("name", v)} placeholder="Jane Smith" required />
        </Field>
      </Row>
      <Row>
        <Field label="Email" required>
          <Input type="email" value={address.email} onChange={(v) => update("email", v)} placeholder="jane@example.com" required />
        </Field>
        <Field label="Phone">
          <Input type="tel" value={address.phone} onChange={(v) => update("phone", v)} placeholder="(314) 555-0100" />
        </Field>
      </Row>

      <SectionLabel>Ship to</SectionLabel>
      <Row single>
        <Field label="Country" required>
          <Select value={address.country} onChange={(v) => update("country", v)}>
            {COUNTRIES.map((c) => <option key={c.code} value={c.code}>{c.name}</option>)}
          </Select>
        </Field>
      </Row>
      <Row single>
        <Field label="Address" required>
          <Input value={address.line1} onChange={(v) => update("line1", v)} placeholder="123 Main St" required />
        </Field>
      </Row>
      <Row single>
        <Field label="Apt, suite, etc.">
          <Input value={address.line2} onChange={(v) => update("line2", v)} placeholder="Optional" />
        </Field>
      </Row>
      <Row>
        <Field label="City" required>
          <Input value={address.city} onChange={(v) => update("city", v)} required />
        </Field>
        <Field label={isUS ? "State" : "Region"} required={isUS}>
          {isUS ? (
            <Select value={address.state} onChange={(v) => update("state", v)} required>
              <option value="">Select…</option>
              {US_STATES.map((s) => <option key={s} value={s}>{s}</option>)}
            </Select>
          ) : (
            <Input value={address.state} onChange={(v) => update("state", v)} placeholder="Region / province" />
          )}
        </Field>
      </Row>
      <Row single>
        <Field label="Postal code" required>
          <Input value={address.postalCode} onChange={(v) => update("postalCode", v)} required />
        </Field>
      </Row>

      {error && (
        <div style={{
          background: "#fdecea", color: "#c0392b", border: "1px solid #f4b7b0",
          padding: "0.75rem 1rem", borderRadius: "8px", fontSize: "0.88rem", margin: "1rem 0",
        }}>{error}</div>
      )}

      {loading && (
        <div style={{
          marginTop: "1.25rem",
          padding: "0.85rem 1rem",
          background: "#f5f8fd",
          border: "1px solid #d0dcea",
          borderRadius: "10px",
          display: "flex",
          alignItems: "center",
          gap: "0.75rem",
          color: "#1d52a0",
          fontSize: "0.9rem",
          fontWeight: 600,
        }}>
          <Spinner />
          <span>{loadingMsg}</span>
        </div>
      )}

      <div style={{ display: "flex", justifyContent: "flex-end", marginTop: "1.5rem" }}>
        <button type="submit" disabled={!canSubmit || loading} style={primaryBtn(!canSubmit || loading)}>
          {loading ? loadingMsg : "Continue →"}
        </button>
      </div>
    </form>
  );
}

function Spinner() {
  return (
    <div style={{
      width: "18px",
      height: "18px",
      border: "2.5px solid #d0dcea",
      borderTopColor: "#1d52a0",
      borderRadius: "50%",
      animation: "jopSpin 0.8s linear infinite",
      flexShrink: 0,
    }}>
      <style>{`@keyframes jopSpin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}

function RatesStep({ rates, selectedRateId, onSelect, onBack, onNext }) {
  return (
    <div>
      <SectionLabel>Shipping options</SectionLabel>
      <p style={{ color: "#666", fontSize: "0.88rem", lineHeight: 1.5, margin: "0 0 1rem" }}>
        Pick the carrier + service you want. Cheapest option is pre-selected.
      </p>
      <div style={{ display: "flex", flexDirection: "column", gap: "0.55rem", marginBottom: "1.5rem" }}>
        {rates.map((r) => (
          <RateRow
            key={r.rateId}
            rate={r}
            selected={r.rateId === selectedRateId}
            onClick={() => onSelect(r.rateId)}
          />
        ))}
      </div>
      <div style={{ display: "flex", gap: "0.6rem", justifyContent: "space-between" }}>
        <button onClick={onBack} style={secondaryBtn(false)}>← Edit address</button>
        <button onClick={onNext} disabled={!selectedRateId} style={primaryBtn(!selectedRateId)}>Review order →</button>
      </div>
    </div>
  );
}

function RateRow({ rate, selected, onClick }) {
  const days = rate.estimatedDays ? `~${rate.estimatedDays} ${rate.estimatedDays === 1 ? "day" : "days"}` : "";
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: "0.75rem",
        padding: "0.75rem 1rem",
        background: selected ? "rgba(29,82,160,0.06)" : "#f9f9fa",
        border: `2px solid ${selected ? "#1d52a0" : "#e0e0e0"}`,
        borderRadius: "10px",
        cursor: "pointer",
        textAlign: "left",
        fontFamily: "inherit",
        color: "#1a1a1a",
      }}
    >
      <div style={{ minWidth: 0 }}>
        <div style={{ fontWeight: 700, fontSize: "0.92rem" }}>
          {rate.provider} <span style={{ color: "#777", fontWeight: 500 }}>· {rate.servicelevel}</span>
        </div>
        {days && <div style={{ color: "#777", fontSize: "0.78rem" }}>{days}</div>}
      </div>
      <div style={{ color: "#1d52a0", fontWeight: 800, fontSize: "1.05rem", flexShrink: 0 }}>
        {formatPrice(rate.amountCents)}
      </div>
    </button>
  );
}

function ReviewStep({ items, address, selectedRate, subtotalCents, couponCode, couponDiscount, onCouponChange, onCouponDiscountChange, onCouponFreeShippingChange, couponFreeShipping, totalCents, onBack, onContinue, busy, error }) {
  const [couponInput, setCouponInput] = useState(couponCode || "");
  const [couponStatus, setCouponStatus] = useState(couponDiscount > 0 ? "applied" : null); // null | "checking" | "applied" | "error"
  const [couponError, setCouponError] = useState(null);
  const [appliedLabel, setAppliedLabel] = useState(couponDiscount > 0 ? `Coupon: ${couponCode}` : "");

  async function applyCoupon() {
    const code = couponInput.trim().toUpperCase();
    if (!code) return;
    setCouponStatus("checking");
    setCouponError(null);
    try {
      const snap = await getDoc(fsDoc(db, "config", "coupons"));
      const codes = snap.data()?.codes || {};
      const match = codes[code];
      if (!match || !match.active) {
        setCouponStatus("error");
        setCouponError("Invalid or expired code");
        onCouponChange("");
        onCouponDiscountChange(0);
        return;
      }
      if (match.maxUses && (match.usedCount || 0) >= match.maxUses) {
        setCouponStatus("error");
        setCouponError("This code has reached its usage limit");
        onCouponChange("");
        onCouponDiscountChange(0);
        return;
      }
      // Calculate discount for display
      const disc = match.type === "percentage"
        ? Math.round(subtotalCents * match.amount / 100)
        : Math.min(match.amount, subtotalCents);
      onCouponChange(code);
      onCouponDiscountChange(disc);
      onCouponFreeShippingChange(!!match.freeShipping);
      const parts = [];
      if (match.type === "percentage") parts.push(`${match.amount}% off`);
      else parts.push(`${formatPrice(match.amount)} off`);
      if (match.freeShipping) parts.push("free shipping");
      setAppliedLabel(parts.join(" + "));
      setCouponStatus("applied");
    } catch (err) {
      setCouponStatus("error");
      setCouponError("Couldn't verify code. Try again.");
    }
  }

  function removeCoupon() {
    setCouponInput("");
    setCouponStatus(null);
    setCouponError(null);
    onCouponChange("");
    onCouponDiscountChange(0);
    onCouponFreeShippingChange(false);
  }

  return (
    <div>
      <SectionLabel>Order summary</SectionLabel>
      <div style={{ background: "#f9f9fa", border: "1px solid #e8e8e8", borderRadius: "10px", padding: "0.9rem 1rem", marginBottom: "1rem" }}>
        {items.map((li) => (
          <div key={li.lineId} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "0.4rem 0", borderBottom: "1px solid #eee" }}>
            <div style={{ minWidth: 0 }}>
              <div style={{ color: "#1a1a1a", fontWeight: 600, fontSize: "0.92rem" }}>{li.name}</div>
              {li.variantName && <div style={{ color: "#777", fontSize: "0.78rem" }}>{li.variantName}</div>}
              <div style={{ color: "#777", fontSize: "0.78rem" }}>× {li.quantity}</div>
            </div>
            <div style={{ color: "#1a1a1a", fontWeight: 700, flexShrink: 0 }}>
              {formatPrice((li.priceCents || 0) * li.quantity)}
            </div>
          </div>
        ))}
        <div style={{ paddingTop: "0.75rem", fontSize: "0.92rem" }}>
          <TotalRow label="Subtotal" value={subtotalCents} />
          {couponDiscount > 0 && (
            <div style={{ display: "flex", justifyContent: "space-between", padding: "0.2rem 0", color: "#70be44" }}>
              <span style={{ fontSize: "0.88rem" }}>Discount ({appliedLabel})</span>
              <span style={{ fontSize: "0.88rem", fontWeight: 600 }}>-{formatPrice(couponDiscount)}</span>
            </div>
          )}
          {couponFreeShipping ? (
            <div style={{ display: "flex", justifyContent: "space-between", padding: "0.2rem 0", color: "#70be44" }}>
              <span style={{ fontSize: "0.88rem" }}>Shipping · {selectedRate.provider} {selectedRate.servicelevel}</span>
              <span style={{ fontSize: "0.88rem", fontWeight: 600, textDecoration: "line-through", color: "#999", marginRight: "0.4rem" }}>{formatPrice(selectedRate.amountCents)}</span>
            </div>
          ) : (
            <TotalRow
              label={`Shipping · ${selectedRate.provider} ${selectedRate.servicelevel}`}
              value={selectedRate.amountCents}
            />
          )}
          <div style={{ borderTop: "2px solid #1d52a0", margin: "0.5rem 0 0", paddingTop: "0.5rem", display: "flex", justifyContent: "space-between" }}>
            <span style={{ color: "#1a1a1a", fontWeight: 800 }}>Total</span>
            <span style={{ color: "#1d52a0", fontWeight: 800, fontSize: "1.1rem" }}>{formatPrice(totalCents)}</span>
          </div>
          <div style={{ color: "#888", fontSize: "0.74rem", textAlign: "right", marginTop: "0.4rem" }}>
            Tax calculated by Square on the payment page
          </div>
        </div>
      </div>

      {/* Coupon code */}
      <div style={{ marginBottom: "1rem" }}>
        {couponStatus === "applied" ? (
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", background: "rgba(112,190,68,0.08)", border: "1px solid rgba(112,190,68,0.35)", borderRadius: "10px", padding: "0.6rem 0.9rem" }}>
            <span style={{ color: "#70be44", fontSize: "0.88rem", fontWeight: 700 }}>✓ {couponInput} — {appliedLabel}</span>
            <button onClick={removeCoupon} style={{ background: "none", border: "none", color: "#888", fontSize: "0.8rem", cursor: "pointer", padding: "0.2rem 0.4rem" }}>Remove</button>
          </div>
        ) : (
          <div style={{ display: "flex", gap: "0.5rem" }}>
            <input
              value={couponInput}
              onChange={e => { setCouponInput(e.target.value.toUpperCase()); if (couponStatus === "error") setCouponStatus(null); }}
              placeholder="Coupon code"
              style={{ flex: 1, background: "#fff", border: `1px solid ${couponStatus === "error" ? "#e74c3c" : "#e0e0e0"}`, borderRadius: "10px", padding: "0.55rem 0.8rem", fontSize: "0.9rem", color: "#1a1a1a", outline: "none", fontFamily: "monospace", letterSpacing: "0.05em" }}
              onKeyDown={e => { if (e.key === "Enter") applyCoupon(); }}
            />
            <button
              onClick={applyCoupon}
              disabled={!couponInput.trim() || couponStatus === "checking"}
              style={{ background: "#1d52a0", color: "#fff", border: "none", borderRadius: "10px", padding: "0.55rem 1rem", fontSize: "0.88rem", fontWeight: 700, cursor: couponInput.trim() ? "pointer" : "default", opacity: couponInput.trim() ? 1 : 0.5 }}
            >
              {couponStatus === "checking" ? "..." : "Apply"}
            </button>
          </div>
        )}
        {couponError && <div style={{ color: "#e74c3c", fontSize: "0.78rem", marginTop: "0.3rem" }}>{couponError}</div>}
      </div>

      <SectionLabel>Ship to</SectionLabel>
      <div style={{ background: "#f9f9fa", border: "1px solid #e8e8e8", borderRadius: "10px", padding: "0.9rem 1rem", marginBottom: "1.25rem", color: "#444", fontSize: "0.9rem", lineHeight: 1.5 }}>
        <strong style={{ color: "#1a1a1a" }}>{address.name}</strong><br />
        {address.line1}{address.line2 ? `, ${address.line2}` : ""}<br />
        {[address.city, address.state, address.postalCode].filter(Boolean).join(", ")}<br />
        {address.country}<br />
        <span style={{ color: "#777", fontSize: "0.82rem" }}>{address.email}</span>
      </div>

      {error && (
        <div style={{ background: "rgba(231,76,60,0.08)", border: "1px solid rgba(231,76,60,0.4)", borderRadius: "10px", padding: "0.7rem 1rem", marginBottom: "0.75rem", color: "#e74c3c", fontSize: "0.88rem" }}>
          {error}
        </div>
      )}

      <div style={{ display: "flex", gap: "0.6rem", justifyContent: "space-between" }}>
        <button onClick={onBack} disabled={busy} style={secondaryBtn(false)}>← Edit shipping</button>
        <button onClick={onContinue} disabled={busy} style={{ ...primaryBtn(false), opacity: busy ? 0.6 : 1 }}>
          {busy ? "Processing..." : totalCents <= 0 ? "Place order →" : "Continue to payment →"}
        </button>
      </div>
    </div>
  );
}

// ── Payment step (Square Web Payments SDK card form) ──────────────────────
function PaymentStep({ totalCents, onBack, onSubmit, busy, error }) {
  const cardRef        = useRef(null);     // DOM node where Square mounts the iframe card form
  const cardInstance   = useRef(null);     // Square.card instance
  const paymentsInstance = useRef(null);   // Square.payments instance
  const [sdkReady, setSdkReady] = useState(false);
  const [sdkError, setSdkError] = useState(null);

  // Initialize SDK + mount card form once
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        // 1. Load SDK
        const Square = await loadSquareSDK();
        if (cancelled) return;
        // 2. Fetch app+location IDs from our backend
        const configFn = httpsCallable(getFunctions(getApp()), "getShopCheckoutConfig");
        const configRes = await configFn({});
        if (cancelled) return;
        const { applicationId, locationId } = configRes.data || {};
        if (!applicationId || !locationId) throw new Error("Missing Square credentials.");
        // 3. Initialize payments
        const payments = Square.payments(applicationId, locationId);
        paymentsInstance.current = payments;
        // 4. Create + attach card form
        const card = await payments.card();
        if (cancelled) return;
        await card.attach(cardRef.current);
        cardInstance.current = card;
        setSdkReady(true);
      } catch (err) {
        if (!cancelled) setSdkError(err?.message || "Payment form failed to load.");
      }
    })();
    return () => {
      cancelled = true;
      // Best-effort teardown so a re-render doesn't try to re-attach
      if (cardInstance.current?.destroy) cardInstance.current.destroy().catch(() => {});
    };
  }, []);

  async function handlePay() {
    if (!cardInstance.current || busy) return;
    try {
      const result = await cardInstance.current.tokenize();
      if (result.status !== "OK") {
        const msg = result.errors?.map((e) => e.message).join(", ") || "Card not accepted.";
        throw new Error(msg);
      }
      // Optionally run buyer verification (3DS/SCA) for EU cards — skipping
      // for v1 US-focused launch; layer in later if we see failures.
      onSubmit({ paymentToken: result.token, verificationToken: null });
    } catch (err) {
      onSubmit.__error?.(err); // no-op; surface via parent error prop instead
      throw err;
    }
  }

  return (
    <div>
      <SectionLabel>Payment</SectionLabel>
      <p style={{ color: "#666", fontSize: "0.88rem", lineHeight: 1.5, margin: "0 0 1rem" }}>
        Secure card entry by Square. Your card details never touch our servers.
      </p>

      {/* Card form container — Square's iframe mounts into this div */}
      <div style={{
        background: "#f9f9fa",
        border: "1px solid #e8e8e8",
        borderRadius: "10px",
        padding: sdkReady ? "1rem" : "0",
        minHeight: "100px",
        marginBottom: "1.25rem",
      }}>
        {!sdkReady && !sdkError && (
          <div style={{ padding: "1.25rem", display: "flex", alignItems: "center", gap: "0.75rem", color: "#1d52a0", fontSize: "0.88rem", fontWeight: 600 }}>
            <Spinner /> <span>Loading secure payment form…</span>
          </div>
        )}
        {sdkError && (
          <div style={{ padding: "1rem", background: "#fdecea", color: "#c0392b", border: "1px solid #f4b7b0", borderRadius: "8px", fontSize: "0.88rem" }}>
            {sdkError}
          </div>
        )}
        <div ref={cardRef} style={{ minHeight: sdkReady ? "64px" : 0 }} />
      </div>

      {error && (
        <div style={{
          background: "#fdecea", color: "#c0392b", border: "1px solid #f4b7b0",
          padding: "0.75rem 1rem", borderRadius: "8px", fontSize: "0.88rem", marginBottom: "1rem",
        }}>{error}</div>
      )}

      <div style={{ display: "flex", gap: "0.6rem", justifyContent: "space-between" }}>
        <button onClick={onBack} disabled={busy} style={secondaryBtn(busy)}>← Review order</button>
        <button
          onClick={handlePay}
          disabled={busy || !sdkReady}
          style={primaryBtn(busy || !sdkReady)}
        >
          {busy ? "Processing…" : `Pay ${formatPrice(totalCents)}`}
        </button>
      </div>
    </div>
  );
}

function TotalRow({ label, value }) {
  return (
    <div style={{ display: "flex", justifyContent: "space-between", padding: "0.2rem 0", color: "#555" }}>
      <span style={{ flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{label}</span>
      <span style={{ flexShrink: 0 }}>{formatPrice(value)}</span>
    </div>
  );
}

// ── Helpers ──

function cleanAddress(a) {
  return {
    name:       a.name.trim(),
    email:      a.email.trim(),
    phone:      a.phone.trim(),
    line1:      a.line1.trim(),
    line2:      a.line2.trim(),
    city:       a.city.trim(),
    state:      a.state.trim(),
    postalCode: a.postalCode.trim(),
    country:    a.country,
  };
}

// ── Form primitives ──

function SectionLabel({ children }) {
  return (
    <div style={{
      fontFamily: "var(--jop-script)",
      fontSize: "1.3rem",
      fontWeight: 600,
      color: "#1d52a0",
      borderBottom: "2px solid #e8e8e8",
      paddingBottom: "0.3rem",
      margin: "1.4rem 0 1rem",
    }}>{children}</div>
  );
}
function Row({ children, single }) {
  return <div style={{
    display: "grid",
    gridTemplateColumns: single ? "1fr" : "1fr 1fr",
    gap: "0.85rem",
    marginBottom: "0.85rem",
  }}>{children}</div>;
}
function Field({ label, required, children }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "0.3rem" }}>
      <label style={{ fontSize: "0.82rem", fontWeight: 500, color: "#444" }}>
        {label}{required && <span style={{ color: "#1d52a0", marginLeft: 2 }}>*</span>}
      </label>
      {children}
    </div>
  );
}
const inputStyle = {
  border: "1.5px solid #d6e3f0",
  borderRadius: "6px",
  padding: "0.55rem 0.75rem",
  fontFamily: "inherit",
  fontSize: "0.95rem",
  color: "#1a1a1a",
  background: "#f9f9fa",
  width: "100%",
  boxSizing: "border-box",
  outline: "none",
  transition: "border-color 0.15s, box-shadow 0.15s, background 0.15s",
};
function Input({ type = "text", value, onChange, placeholder, required }) {
  const [focused, setFocused] = useState(false);
  return (
    <input
      type={type} value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder} required={required}
      onFocus={() => setFocused(true)} onBlur={() => setFocused(false)}
      style={{
        ...inputStyle,
        background: focused ? "#fff" : inputStyle.background,
        borderColor: focused ? "#1d52a0" : "#d6e3f0",
        boxShadow: focused ? "0 0 0 3px rgba(29,82,160,0.15)" : "none",
      }}
    />
  );
}
function Select({ value, onChange, required, children }) {
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      required={required}
      style={{
        ...inputStyle, cursor: "pointer", appearance: "none", paddingRight: "2.2rem",
        backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='8' viewBox='0 0 12 8'%3E%3Cpath fill='%231d52a0' d='M6 8L0 0h12z'/%3E%3C/svg%3E")`,
        backgroundRepeat: "no-repeat", backgroundPosition: "right 0.85rem center",
      }}
    >{children}</select>
  );
}
function primaryBtn(disabled) {
  return {
    background: disabled ? "#d0d0d0" : "#fcb31f",
    color: "#1a0a12",
    fontFamily: "var(--jop-script)",
    fontSize: "1.2rem",
    fontWeight: 700,
    padding: "0.7rem 1.7rem",
    border: "none",
    borderRadius: "999px",
    cursor: disabled ? "default" : "pointer",
    letterSpacing: "0.02em",
    boxShadow: disabled ? "none" : "0 4px 16px rgba(252,179,31,0.4)",
  };
}
function secondaryBtn(disabled) {
  return {
    background: "transparent",
    color: "#1d52a0",
    border: "1px solid #d0dcea",
    borderRadius: "999px",
    padding: "0.6rem 1.2rem",
    fontFamily: "var(--jop-display)",
    fontSize: "0.9rem",
    fontWeight: 600,
    cursor: disabled ? "default" : "pointer",
  };
}
